import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ShoppingBag, Search, Tag, Package, AlertTriangle, Plus, Minus, Trash2, X, ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api' });

export default function PublicShopPage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);

  const addToCart = (product, e) => {
    if (e) e.stopPropagation();
    setCart(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) return prev.map(i => i.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { id: product.id, name: product.name, price: product.result_value || product.price_per_unit || 0, unit: product.unit, qty: 1 }];
    });
  };
  const updateQty = (id, qty) => {
    if (qty <= 0) setCart(prev => prev.filter(i => i.id !== id));
    else setCart(prev => prev.map(i => i.id === id ? { ...i, qty } : i));
  };
  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const toggleLang = () => { const next = i18n.language === 'pl' ? 'en' : 'pl'; i18n.changeLanguage(next); localStorage.setItem('swibi_language', next); };

  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (category) params.category = category;
    api.get(`/shop/public/${slug}`, { params })
      .then(r => {
        setShop(r.data.settings);
        setProducts(r.data.products);
        setCategories(r.data.categories);
        setError(null);
      })
      .catch(err => setError(err.response?.status === 404 ? 'notfound' : 'error'))
      .finally(() => setLoading(false));
  }, [slug, search, category]);

  const [configVars, setConfigVars] = useState([]);
  const [configItems, setConfigItems] = useState([]);
  const [configTotal, setConfigTotal] = useState(0);
  const [calculating, setCalculating] = useState(false);
  const calcTimer = React.useRef(null);

  const loadProduct = async (id) => {
    try {
      const r = await api.get(`/shop/public/${slug}/product/${id}`);
      setSelectedProduct(r.data.product);
      if (r.data.local_variables?.length > 0) {
        setConfigVars(r.data.local_variables);
        setConfigItems(r.data.configurator_items || []);
        // Initial calculation
        recalculate(r.data.local_variables);
      } else {
        setConfigVars([]);
        setConfigItems([]);
      }
    } catch {}
  };

  const recalculate = async (vars) => {
    if (!selectedProduct && !vars) return;
    setCalculating(true);
    try {
      const variables = {};
      (vars || configVars).forEach(v => { variables[v.key] = parseFloat(v.value) || 0; });
      const productId = selectedProduct?.id || vars?.[0]?._productId;
      if (!productId) return;
      const r = await api.post(`/shop/public/${slug}/product/${selectedProduct?.id}/calculate`, { variables });
      setConfigItems(r.data.items || []);
      setConfigTotal(r.data.total || 0);
    } catch {}
    finally { setCalculating(false); }
  };

  const handleConfigVarChange = (key, newValue) => {
    const updated = configVars.map(v => v.key === key ? { ...v, value: newValue } : v);
    setConfigVars(updated);
    // Debounce recalculation
    if (calcTimer.current) clearTimeout(calcTimer.current);
    calcTimer.current = setTimeout(() => recalculate(updated), 400);
  };

  const fmt = (v) => Number(v || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 });

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-400">{t('common.loading')}</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
        <AlertTriangle size={48} className="text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">Sklep niedostępny</h2>
        <p className="text-gray-500">Sklep nie istnieje lub jest nieaktywny.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingBag size={24} className="text-red-600" />
            <h1 className="text-xl font-bold text-gray-800">{shop?.shop_title || 'Sklep'}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggleLang} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 border border-gray-200 rounded">
              {i18n.language === 'pl' ? 'EN' : 'PL'}
            </button>
            <button onClick={() => setShowCart(!showCart)} className="relative flex items-center gap-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium">
              <ShoppingCart size={16} />
              {cartCount > 0 && <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white text-red-600 text-xs font-bold rounded-full flex items-center justify-center border-2 border-red-600">{cartCount}</span>}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Search + Filters */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('common.search') + '...'} className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm" />
          </div>
          {categories.length > 0 && (
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm">
              <option value="">Wszystkie kategorie</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>

        {/* Product detail */}
        {selectedProduct && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
            <button onClick={() => { setSelectedProduct(null); setConfigVars([]); setConfigItems([]); }} className="text-sm text-gray-500 hover:text-gray-700 mb-4">← Powrót do listy</button>
            <div className="flex gap-8">
              {selectedProduct.image && (
                <img src={`/uploads/pricelists/${selectedProduct.image}`} alt={selectedProduct.name}
                  className="w-64 h-64 object-cover rounded-lg border" />
              )}
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{selectedProduct.name}</h2>
                {selectedProduct.sku && <div className="text-sm text-gray-400 mb-2">SKU: {selectedProduct.sku}</div>}
                {selectedProduct.description && <p className="text-gray-600 mb-4">{selectedProduct.description}</p>}

                {/* Configurator mode */}
                {configVars.length > 0 ? (
                  <div className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h3 className="text-sm font-bold text-amber-800 mb-3">Konfiguracja</h3>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                        {configVars.map(v => (
                          <div key={v.key} className="flex items-center justify-between gap-2">
                            <label className="text-sm text-gray-600 truncate">{v.description || v.key}</label>
                            <input type="number" value={v.value}
                              onChange={e => handleConfigVarChange(v.key, e.target.value)}
                              className="w-24 px-2 py-1 border border-amber-300 rounded text-sm text-right font-mono bg-white focus:ring-1 focus:ring-amber-400" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Calculation results */}
                    {shop?.show_prices && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="space-y-1">
                          {configItems.filter(i => i.parent_id).map(item => (
                            <div key={item.id} className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">{item.name}</span>
                              <span className="font-mono text-gray-700">{fmt(item.result_value)} {shop?.currency_display || 'PLN'}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-300 flex items-center justify-between">
                          <span className="text-lg font-bold text-gray-800">Razem:</span>
                          <span className="text-2xl font-bold text-red-600">
                            {calculating ? '...' : fmt(configTotal)} {shop?.currency_display || 'PLN'}
                          </span>
                        </div>
                      </div>
                    )}

                    <button onClick={() => addToCart({ ...selectedProduct, result_value: configTotal, configVars: configVars.map(v => ({ key: v.key, value: v.value, description: v.description })) })}
                      className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium">
                      <ShoppingCart size={18} /> Dodaj do koszyka
                    </button>
                  </div>
                ) : (
                  <>
                    {shop?.show_prices && selectedProduct.result_value > 0 && (
                      <div className="text-2xl font-bold text-red-600 mb-4">
                        {fmt(selectedProduct.result_value)} {shop?.currency_display || 'PLN'}
                        {selectedProduct.unit && <span className="text-sm text-gray-400 font-normal ml-1">/ {selectedProduct.unit}</span>}
                      </div>
                    )}
                    <button onClick={() => addToCart(selectedProduct)}
                      className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium">
                      <ShoppingCart size={18} /> Dodaj do koszyka
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Products grid */}
        {!selectedProduct && (
          <>
            {products.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Package size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg">{search ? t('common.noResults') : 'Brak produktów'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map(p => (
                  <div key={p.id} onClick={() => loadProduct(p.id)}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition cursor-pointer group">
                    {p.image ? (
                      <div className="aspect-square bg-gray-100">
                        <img src={`/uploads/pricelists/${p.image}`} alt={p.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition" />
                      </div>
                    ) : (
                      <div className="aspect-square bg-gray-100 flex items-center justify-center">
                        <Package size={40} className="text-gray-300" />
                      </div>
                    )}
                    <div className="p-3">
                      <h3 className="font-medium text-gray-800 text-sm truncate">{p.name}</h3>
                      {p.shop_category && (
                        <span className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><Tag size={10} />{p.shop_category}</span>
                      )}
                      {shop?.show_prices && p.result_value > 0 && (
                        <div className="font-bold text-red-600 mt-1">
                          {fmt(p.result_value)} {shop?.currency_display || 'PLN'}
                        </div>
                      )}
                      <button onClick={(e) => addToCart(p, e)}
                        className="mt-2 w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium opacity-0 group-hover:opacity-100 transition">
                        <Plus size={12} /> Do koszyka
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 pt-8 pb-4">
          Wygenerowano w serwisie <a href="https://swibi.net" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">SWIBI.net</a>
        </div>
      </div>

      {/* Cart panel */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowCart(false)} />
          <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><ShoppingCart size={20} /> Koszyk</h2>
              <button onClick={() => setShowCart(false)} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-auto p-5">
              {cart.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <ShoppingCart size={40} className="mx-auto mb-3 opacity-50" />
                  <p>Koszyk jest pusty</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cart.map(item => (
                    <div key={item.id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 text-sm truncate">{item.name}</div>
                        <div className="text-xs text-gray-500">{fmt(item.price)} {shop?.currency_display || 'PLN'}{item.unit ? ` / ${item.unit}` : ''}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => updateQty(item.id, item.qty - 1)} className="w-7 h-7 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-100">
                          <Minus size={12} />
                        </button>
                        <span className="w-8 text-center text-sm font-medium">{item.qty}</span>
                        <button onClick={() => updateQty(item.id, item.qty + 1)} className="w-7 h-7 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-100">
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="text-sm font-bold text-gray-800 w-20 text-right">{fmt(item.price * item.qty)}</div>
                      <button onClick={() => updateQty(item.id, 0)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {cart.length > 0 && (
              <div className="border-t px-5 py-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-600 font-medium">Razem:</span>
                  <span className="text-xl font-bold text-gray-800">{fmt(cartTotal)} {shop?.currency_display || 'PLN'}</span>
                </div>
                <button className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold">
                  Złóż zamówienie
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
